// Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'
import { flatten } from 'lodash'

import {
  TabbedView,
  ScopedTabbedView,
  EmployerPayrollCycleTabs
} from '../../'

import {
  getNodeTypeFromBase64Id,
  getUniqueValuesInArrayOfObjectsByProperty
} from '../../../utils'

const identityTypeToPrimaryTabsMapping = {
  'client': [
    // { name: 'summary', label: 'Summary' },
    // { name: 'earnbacks', label: 'EarnBacks' },
    { name: 'general', label: 'General' },
    { name: 'contact', label: 'Contact' },
    // { name: 'banking', label: 'Banking' },
    // { name: 'products', label: 'Products' },
    { name: 'requests', label: 'Requests' },
    // { name: 'cases', label: 'Cases' },
    // { name: 'payroll', label: 'Payroll' },
    // { name: 'documents', label: 'Documents' },
    { name: 'communications', label: 'Communications' },
    // { name: 'accounts', label: 'Accounts' },
    // { name: 'insights', label: 'Insights' }
  ]
}

const getPrimaryTab = (tab) => (
  {
    'credit': 'products',
    'investments': 'products',
    'insurance': 'products',
    'sms_send': 'communications',
    'sms': 'communications',
    // 'whatsapp': 'communications',
    'latest': 'insights',
    'comparison': 'insights',
    'payslips': 'payroll'
  }[tab] ||
  (
    getNodeTypeFromBase64Id(tab) === 'PayrollCycleNode'
      ? 'payrollcycles'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'LoanNode' || getNodeTypeFromBase64Id(tab) === 'CreditNode'
      ? 'credit'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'ClientInstrumentNode'
      ? 'investments'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'ClientFuneralPolicyNode'
      ? 'insurance'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'CaseNode'
      ? 'cases'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'RequestRunNode'
      ? 'requests'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'AccountNode'
      ? 'accounts'
      : null
  ) ||
  (
    getNodeTypeFromBase64Id(tab) === 'WhatsappMessageNode'
      ? 'communications'
      : null
  )
)

const hasSecondaryTabs = (tabName) => (
  ['products', 'communications', 'payrollcycles', 'insights', 'payroll'].indexOf(tabName) > -1
)

const getActivePrimaryTab = (initialTab) => {
  if (initialTab) {
    return getUniqueValuesInArrayOfObjectsByProperty(
      flatten(Object.values(identityTypeToPrimaryTabsMapping)),
      'name'
    ).indexOf(initialTab) > -1
      ? initialTab
      : getPrimaryTab(initialTab) || 'general'
  } else {
    return null
  }
}


class EmployeePageNavigation extends React.PureComponent {
  static propTypes = {
    employerId: PropTypes.string.isRequired,
    identityId: PropTypes.string.isRequired,
    identityTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    onTabClick: PropTypes.func.isRequired,
    initialTab: PropTypes.string
  }

  state = {
    // assign activePrimaryTab props.initialTab's value if that value is an available primary listed in identityTypeToPrimaryTabsMapping
    activePrimaryTab: getActivePrimaryTab(this.props.initialTab)
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.initialTab !== this.props.initialTab) {
      this.setState({ activePrimaryTab: getActivePrimaryTab(this.props.initialTab) })
    }
  }

  getPrimaryTabs = (identityTypes) => {
    let allTabs = []

    Object.keys(identityTypeToPrimaryTabsMapping).forEach(identityType => {
      if (identityTypes.includes(identityType)) {
        allTabs = [...allTabs, ...identityTypeToPrimaryTabsMapping[identityType]]
      }
    })

    if (identityTypes.length > 1) {
      // if identity has multiple identity types, get unique tabs across identity types to display
      const uniqueTabs = allTabs.filter((tab, index) => (
        index === allTabs.findIndex(obj => (
          JSON.stringify(obj) === JSON.stringify(tab)
        ))
      ))
      return uniqueTabs
    } else {
      return allTabs
    }
  }

  getSecondaryTabs = (primaryTab) => {
    if (primaryTab === 'payrollcycles') {
      return (
        <Query
          query={
            gql`
              query($id: ID!) {
                identity(id: $id) {
                  id
                  employer {
                    id
                  }
                }
              }
            `
          }
          variables={{ id: this.props.identityId }}>
          {({ loading, error, data}) => {
            if (loading || error) {
              return null
            }

            return (
              <EmployerPayrollCycleTabs
                employerId={data.identity.employer.id}
                onClick={payrollCycleId => {
                  this.props.history.push(`${this.props.match.url}/payrollcycles/${payrollCycleId}`)
                  this.props.onTabClick('payrollcycles', payrollCycleId)
                }}
                initialActiveTab={this.props.initialTab}
              />
            )
          }}
        </Query>
      )
    } else if (primaryTab === 'communications'){
      let subTabs = [
        { label: 'Send SMS', path: 'sms_send' },
        { label: 'SMS messages', path: 'sms' },
        // { label: 'WhatsApp messages', path: 'whatsapp' }
      ]
      let currentTab = this.props.location.pathname.split('/').pop().toLowerCase()
      if (currentTab === 'communications') {
        this.props.history.push(
          `${this.props.match.url}/communications/sms_send`
        )
        this.props.onTabClick('sms_send')  // initialise default view
      }
      let initialActiveTabIndex = ['sms_send', 'sms', 'whatsapp'].indexOf(currentTab)

      return (
        <ScopedTabbedView
          tabs={subTabs.map(tab => tab.label)}
          contents={[]}
          onClick={tabIndex => {
            let productTab = subTabs[tabIndex].path
            this.props.history.push(
              `${this.props.match.url}/communications/${productTab}`
            )
            this.props.onTabClick(productTab)
          }}
          initialActiveTabIndex={initialActiveTabIndex > -1
            ? initialActiveTabIndex
            : null
          }
        />
      )
    } else if (primaryTab === 'payroll'){
      let subTabs = ['Payroll runs', 'Payslips']
      let currentTab = this.props.location.pathname.split('/').pop().toLowerCase()
      if (currentTab === 'payroll') {
        this.props.history.push(
          `${this.props.match.url}/payroll/payrollruns`
        )
        this.props.onTabClick('payrollruns')  // initialise default view
      }
      let initialActiveTabIndex = ['payrollruns', 'payslips'].indexOf(currentTab)

      return (
        <ScopedTabbedView
          tabs={subTabs}
          contents={[]}
          onClick={tabIndex => {
            let productTab = subTabs[tabIndex].toLowerCase().replace(' ', '')
            this.props.history.push(
              `${this.props.match.url}/payroll/${productTab}`
            )
            this.props.onTabClick(productTab)
          }}
          initialActiveTabIndex={initialActiveTabIndex > -1
            ? initialActiveTabIndex
            : null
          }
        />
      )
    } else {
      return null
    }
  }

  render() {
    let primaryTabs = this.getPrimaryTabs(this.props.identityTypes)
    let primaryTabNames = Object.values(primaryTabs).map(tab => tab.name)
    let primaryTabLabels = Object.values(primaryTabs).map(tab => tab.label)
    let selectedTab = primaryTabNames.indexOf(this.state.activePrimaryTab)

    return (
      <React.Fragment>
        <div className='u-bg-color-white'>
          <TabbedView
            tabs={primaryTabLabels}
            contents={[]}
            selectedTab={selectedTab}
            onClick={tabIndex => {
              let tab = primaryTabs[tabIndex].name
              this.setState({ activePrimaryTab: tab }, () => (
                this.props.history.push(`${this.props.match.url}/${tab}`)
              ))
              if (hasSecondaryTabs(tab)) {
                this.props.onTabClick(null)
              } else {
                this.props.onTabClick(tab)
              }
            }}
            tabsClassName='slds-align_absolute-center'
            alwaysFireOnClick={true}
          />
        </div>
        {this.getSecondaryTabs(this.state.activePrimaryTab)}
      </React.Fragment>
    )
  }
}

export default withRouter(EmployeePageNavigation)



// WEBPACK FOOTER //
// ./src/components/messageplus/employee-page-navigation/index.js